import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import classnames from 'classnames/bind';
import { connect } from 'react-redux';

import { get, pageContext, getRulesFromState, applyVerticalStyles, shapedPropsWarning } from 'ctm-util-functions';

import { FadeIn, SlideDown } from '../animations';

import { AffixWrapper } from '../../higher_order_components/affix_wrapper';

import componentStyles from './flexbox.css';
import alignmentStyles from './flex_alignment.css';
import colStyles from './col.css';
import rowStyles from './row.css';
import containerStyles from './container.css';

const styles = { ...containerStyles, ...rowStyles, ...colStyles, ...componentStyles, ...alignmentStyles };
const cx = classnames.bind(styles);

const BreakpointType = PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl', true, false]);

// @todo: Avoid adding here where possible -- instead, consider a variant, or
// not using flexbox in your component - use a div and apply styles instead.
const positionList = [
    'auto', 'all', 'top', 'bottom', 'left', 'right', 'top_bottom',
    'top_bottom_small', 'top_xsmall', 'top_small', 'top_medium', 'top_xxlarge', 'top_auto', 'left_auto', 'right_left', 'relative', 'right_xsmall',
    'left_xsmall', 'left_small',
    'right_small', 'right_auto', 'all_small', 'left_right', 'bottom_right', 'bottom_xsmall', 'bottom_xxlarge',
    'left_right_xsmall', 'left_right_small', 'all_xsmall', 'all_large', 'left_right_xlarge', false, 'none'
];
const PositionType = PropTypes.oneOf(positionList);
const FlexValue = PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8]);

const IntOrBoolType = PropTypes.oneOfType([PropTypes.number, PropTypes.bool]);
const classKeys = [
    'contentStart', 'contentCenter', 'contentEnd', 'contentAround', 'contentBetween',
    'row', 'column',
    'alignSelfStart', 'alignSelfCenter', 'alignSelfEnd', 'alignSelfStretch',
    'itemsTop', 'itemsCenter', 'itemsBottom',
    'xs', 'sm', 'md', 'lg', 'xl', 'xxl',
    'offset', 'xsOffset', 'smOffset', 'mdOffset', 'lgOffset', 'xlOffset',
    'xsOrder', 'smOrder', 'mdOrder', 'lgOrder', 'xlOrder',
    'border', 'margin', 'padding', 'display', 'bgColor', 'position', 'flex', 'gapless'
];

/**
 * Flexbox grid component
 * This component is used by the layout builder to create our grid layout
 * and layout our components.
 * It has mapped state to props to enable using the rules on a layout schema i.e.
 *     "rules": [
 *     {
 *       "controls": "movingIn",
 *       "rule": "movingIn"
 *     }]
 * @returns {Node} Flexbox A flexbox component
 */
class _Flexbox extends Component {

    constructor(props) {
        super(props);
        // @todo remove once all shapedProps are gone
        shapedPropsWarning(props, 'Flexbox');
        this.styles = applyVerticalStyles(styles, 'flexbox');
    }

    /**
     * @desc map visualProps to classes
     * @returns {String} - className string
     */
    getClassName = () => {
        const { className } = this.props;
        const combinedStyles = this.styles;
        const otherClasses = [];
        const { container, containerFluid, rowReverse, columnReverse, wrap, variant } = this.props;
        const generatedClassName = cx({
            container,
            containerFluid,
            rowReverse,
            columnReverse,
            wrap
        });

        for (const key of classKeys) {
            // eslint-disable-next-line react/destructuring-assignment
            const value = this.props[key];
            if (value || value === 0) {
                let name = '';
                const values = Array.isArray(value) ? value : [value];
                values.forEach((pos) => {
                    if (Array.isArray(value)) {
                        name = `${key}_${pos}`;
                    } else {
                        name = value === true ? `${key}` : `${key}_${value}`;
                    }

                    if (combinedStyles[name]) {
                        otherClasses.push(combinedStyles[name]);
                    } else {
                        // helps us keep valid schemas by showing when invalid class is applied
                        otherClasses.push('undefined');
                    }
                });
            }
        }

        if (variant && combinedStyles[`variant_${variant}`]) {
            otherClasses.push(combinedStyles[`variant_${variant}`]);
        }

        return cx((className || null), (generatedClassName || null), ...otherClasses) || null;
    };

    /**
     * Determines what tag name to return, between a supplied tag name, default 'div', or the
     * Fragment component. (The fragment component will only ever return if no className exists)
     * @returns {Fragment|String} - a HTML tag name or React.Fragment
     */
    getTagName = () => {
        let { tagName } = this.props;
        if (!tagName) {
            tagName = !this.getClassName() ? Fragment : 'div';
        }
        return tagName;
    };

    /**
     * @desc map visualProps to classes
     * @returns {Object} Rendered Flexbox
     */
    render = () => {
        // Pass element props from schema to flexbox for e.g. role attributes
        const {
            className,
            tagName: ignored,
            role,
            layoutAreaKey,
            extends: schemaExtends,
            parentLayoutAreaKey,
            children,
            ...visualProps
        } = this.props;
        const tagName = this.getTagName();

        let componentProps = {};
        // Fragment doesn't accept props other than key and children
        if (tagName !== Fragment) {
            componentProps = { className: this.getClassName(), role };
            componentProps['data-layout-area-key'] = layoutAreaKey || null;
            componentProps['data-layout-extends'] = schemaExtends || null;
            componentProps['data-parent-area-key'] = parentLayoutAreaKey || null;
        }

        let component = React.createElement(
            tagName,
            componentProps,
            children
        );

        // if we've specified in the schema that this is an 'affixable' container, wrap the component in a
        // affix wrapper to handle 'affixing'
        if (visualProps.affix) {
            component = (
                <AffixWrapper {...visualProps}>
                    {component}
                </AffixWrapper>
            );
        }

        // animations
        // @todo really need to figure out a better way to do this rather than a long chain of if statements, (this will be fixed in another ticket item)
        if (visualProps.fadeIn) {
            component = (
                <div className={componentStyles.variant_content_root}>
                    <FadeIn>
                        {component}
                    </FadeIn>
                </div>
            );
        }

        // @todo animation wrapping to be fixed in a new ticket
        if (get(visualProps, 'animation.type') === 'slideDown') {
            component = (
                <SlideDown offset={visualProps.animation.offset }>
                    <div className={componentStyles.variant_content_root}>
                        {component}
                    </div>
                </SlideDown>
            );
        }

        const isVisibleFromRules = get(this.props, 'rules.visibility.result');
        const isVisible = (typeof isVisibleFromRules === 'undefined' || isVisibleFromRules === null) ? true : isVisibleFromRules;
        return isVisible ? component : null;
    };

}

_Flexbox.propTypes = {
    /**
     * element props
     */
    tagName: PropTypes.string,
    role: PropTypes.string,
    /**
     * Common visual props
     */
    variant: PropTypes.string,
    display: PropTypes.string,

    /**
     * Wrapper props
     */
    container: PropTypes.bool,
    containerFluid: PropTypes.bool,
    row: BreakpointType,
    rowReverse: PropTypes.bool,
    column: BreakpointType,
    columnReverse: PropTypes.bool,
    affix: PropTypes.bool,
    wrap: PropTypes.bool,

    /**
     * Gapless for both row and columns
     * e.g. gapless: "sm" // gapless from sm breakpoint
     */
    gapless: BreakpointType,

    /**
     * Flex alignment props
     */
    contentStart: BreakpointType,
    contentCenter: BreakpointType,
    contentEnd: BreakpointType,

    itemsTop: BreakpointType,
    itemsCenter: BreakpointType,
    itemsBottom: BreakpointType,

    contentAround: BreakpointType,
    contentBetween: BreakpointType,

    /**
     * Flex sizing props
     */
    xs: IntOrBoolType,
    sm: IntOrBoolType,
    md: IntOrBoolType,
    lg: IntOrBoolType,
    xl: IntOrBoolType,
    xxl: IntOrBoolType,

    xsOffset: PropTypes.number,
    smOffset: PropTypes.number,
    mdOffset: PropTypes.number,
    lgOffset: PropTypes.number,
    xlOffset: PropTypes.number,
    xxlOffset: PropTypes.number,

    xsOrder: PropTypes.number,
    smOrder: PropTypes.number,
    mdOrder: PropTypes.number,
    lgOrder: PropTypes.number,
    xlOrder: PropTypes.number,
    xxlOrder: PropTypes.number,

    /**
     * Flex sizing shorthand values
     */
    flex: FlexValue,

    /**
     * Container props
     */
    border: PositionType,
    margin: PropTypes.oneOfType([PositionType, PropTypes.array]),
    padding: PropTypes.oneOfType([PositionType, PropTypes.array]),
    bgColor: PropTypes.string,
    position: PositionType,

    children: PropTypes.node,
    className: PropTypes.string,
    layoutAreaKey: PropTypes.string,
    extends: PropTypes.string,
    parentLayoutAreaKey: PropTypes.string
};

/**
 * mapStateToProps - sets up the rules required by this component
 * @param {Object} state - The entire application state
 * @param {Object} props - The props for the form group this is being applied to
 * @returns {Object}     - Props for the component, mapped from state values
 */
const mapStateToProps = (state, props) => {
    const rules = getRulesFromState(state, props);

    if (Object.keys(rules).length !== 0) {
        return { rules };
    }

    return {};
};

// Connect the component to the Redux store.
const Flexbox = connect(mapStateToProps)(_Flexbox);

// Export the Enhanced component.
export { _Flexbox as SimpleFlexbox, Flexbox };
